import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadICSCalendar } from '../lib/icsCalendar'
import { getSetting } from '../lib/apiPhpAdapter'
import './WeeklyNews.css'

const CALENDAR_PREVIEW_LIMIT = 3

const API_BASE_URL = window.location.origin + '/api'

interface NewsItem {
  id: string
  title: string
  content: string
  date?: string
}

interface WeeklyNewsData {
  title: string
  news: NewsItem[]
}

interface WeeklyNewsResponse {
  news: WeeklyNewsData
  calendarUrl?: string | null
}

type WeeklyNewsProps = {
  /** false = jen editovatelné novinky (bez „Akce z kalendáře školy“). */
  showSchoolCalendar?: boolean
  className?: string
}

export default function WeeklyNews({ showSchoolCalendar = true, className }: WeeklyNewsProps) {
  const navigate = useNavigate()
  const [newsData, setNewsData] = useState<WeeklyNewsData>({
    title: 'Aktuální novinky týdne',
    news: []
  })
  const [calendarEvents, setCalendarEvents] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedNews, setExpandedNews] = useState<Set<string>>(new Set())
  const [expandedCalendar, setExpandedCalendar] = useState<Set<string>>(new Set())
  const visibleCalendarEvents = calendarEvents.slice(0, CALENDAR_PREVIEW_LIMIT)
  const hasMoreCalendarEvents = calendarEvents.length > CALENDAR_PREVIEW_LIMIT

  const toggleNews = (id: string) => {
    setExpandedNews(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const toggleCalendar = (id: string) => {
    setExpandedCalendar(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  useEffect(() => {
    loadNews()
    if (showSchoolCalendar) {
      loadCalendarUrl()
    }
  }, [showSchoolCalendar])

  // Загружаем URL календаря из настроек
  const loadCalendarUrl = async () => {
    try {
      const url = await getSetting('calendar_url')
      if (url) {
        await loadCalendarEvents(url)
        return
      }
    } catch (error) {
      // Игнорируем ошибки загрузки настроек - используем URL по умолчанию
    }
    
    // Используем URL по умолчанию из CalendarView
    const defaultUrl = 'https://outlook.office365.com/owa/calendar/b86242d5bf2d4496a89c04f9eaf4cc69@iss-cheb.cz/6d0809beabcb451898dddfc17196b3433773793055790110233/calendar.ics'
    try {
      await loadCalendarEvents(defaultUrl)
    } catch (defaultError) {
      console.error('WeeklyNews: Error loading calendar events:', defaultError)
    }
  }

  // Загружаем события календаря на текущую неделю (с субботы по субботу)
  const loadCalendarEvents = async (url: string) => {
    try {
      // Определяем неделю: с субботы по субботу
      const now = new Date()
      const dayOfWeek = now.getDay() // 0 = воскресенье, 6 = суббота
      
      let saturday = new Date(now)
      if (dayOfWeek === 6) {
        // Если сегодня суббота
        const hour = now.getHours()
        if (hour >= 18) {
          // После 18:00 показываем следующую неделю (начинаем с сегодняшней субботы)
        } else {
          // До 18:00 показываем текущую неделю (с прошлой субботы)
          saturday.setDate(saturday.getDate() - 7)
        }
      } else if (dayOfWeek === 0) {
        // Если воскресенье, берем вчерашнюю субботу
        saturday.setDate(saturday.getDate() - 1)
      } else {
        // Для остальных дней находим предыдущую субботу
        const daysToSubtract = dayOfWeek + 1
        saturday.setDate(saturday.getDate() - daysToSubtract)
      }
      saturday.setHours(0, 0, 0, 0)
      
      // Находим следующую субботу (конец недели)
      const nextSaturday = new Date(saturday)
      nextSaturday.setDate(nextSaturday.getDate() + 7)
      nextSaturday.setHours(23, 59, 59, 999)
      
      // Загружаем события через тот же API, что и календарь
      const events = await loadICSCalendar(url, saturday, nextSaturday)
      
      // Преобразуем события в формат новостей
      const newsItems: NewsItem[] = events.map(event => {
        const eventDate = new Date(event.start.dateTime)
        const formattedDate = eventDate.toLocaleDateString('cs-CZ', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric'
        })
        
        return {
          id: 'calendar_' + event.id,
          title: event.subject,
          content: event.body?.content || `Akce se koná dne ${formattedDate}`,
          date: formattedDate
        }
      })
      
      setCalendarEvents(newsItems)
    } catch (error) {
      console.error('Error loading calendar events:', error)
      setCalendarEvents([])
    }
  }

  const loadNews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/weekly-news`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })

      if (response.ok) {
        const data: WeeklyNewsResponse = await response.json()
        console.log('WeeklyNews: Received data:', data)
        if (data && data.news) {
          setNewsData(data.news)
        }
      } else {
        // Если ошибка, просто используем пустые данные
        console.warn('Failed to load weekly news:', response.status)
      }
    } catch (error) {
      console.error('Error loading weekly news:', error)
      // При ошибке используем пустые данные
      setNewsData({
        title: 'Aktuální novinky týdne',
        news: []
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={['weekly-news', className].filter(Boolean).join(' ')}>
        <div className="weekly-news-loading">Načítání novinek...</div>
      </div>
    )
  }

  // Всегда показываем панель, даже если нет новостей (для IT администраторов будет видна возможность редактирования)
  // if (!newsData.news || newsData.news.length === 0) {
  //   return null // Не показываем панель, если нет новостей
  // }

  return (
    <div className={['weekly-news', className].filter(Boolean).join(' ')}>
      <div className="weekly-news-container">
        <div className="weekly-news-section">
          <h3 className="weekly-news-title">{newsData.title}</h3>
          {newsData.news && newsData.news.length > 0 ? (
            <div className="weekly-news-list">
              {newsData.news.map((item) => {
                const isExpanded = expandedNews.has(item.id)
                return (
                  <div key={item.id} className="weekly-news-item">
                    <div
                      className="news-item-header"
                      onClick={() => toggleNews(item.id)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <h4 className="news-item-title">
                        {item.title}
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#666' }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </h4>
                      {item.date && <span className="news-item-date">{item.date}</span>}
                    </div>
                    {isExpanded && <p className="news-item-content">{item.content}</p>}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="weekly-news-empty">
              <p>Zatím žádné novinky.</p>
            </div>
          )}
        </div>

        {showSchoolCalendar ? (
          <div className="weekly-news-section weekly-news-calendar-section">
            <h3 className="weekly-news-title weekly-news-calendar-title">Akce z kalendáře školy</h3>
            {calendarEvents && calendarEvents.length > 0 ? (
              <>
                <div className="weekly-news-list">
                  {visibleCalendarEvents.map((item) => {
                    const isExpanded = expandedCalendar.has(item.id)
                    return (
                      <div key={item.id} className="weekly-news-item weekly-news-calendar-item">
                        <div
                          className="news-item-header"
                          onClick={() => toggleCalendar(item.id)}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          <h4 className="news-item-title">
                            {item.title}
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.8em', color: '#666' }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </h4>
                          {item.date && <span className="news-item-date">{item.date}</span>}
                        </div>
                        {isExpanded && <p className="news-item-content">{item.content}</p>}
                      </div>
                    )
                  })}
                </div>
                {hasMoreCalendarEvents && (
                  <button
                    type="button"
                    className="weekly-news-more-link"
                    onClick={() => navigate('/calendar')}
                  >
                    Zobrazit další ({calendarEvents.length - CALENDAR_PREVIEW_LIMIT})
                  </button>
                )}
              </>
            ) : (
              <div className="weekly-news-empty">
                <p>Zatím žádné akce na tento týden.</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

